/**
 * Utility to check a value is usable (not null/undefined/empty string)
 */
const hasValue = (val) => val !== undefined && val !== null && String(val).trim() !== '';

/**
 * Recursively unwrap common payload wrappers until we reach the deepest object that holds fields.
 * This is fail-safe: it walks through known wrapper keys, and also returns the original object.
 * Enhanced to search more wrapper paths like Sucuri does.
 */
const unwrapPayload = (input) => {
  if (!input || typeof input !== 'object') return input;
  const chain = ['data', 'result', 'payload', 'scan', 'securityInfo', 'monitor', 'server', 'meta', 'overall', 'site', 'environment', 'env'];
  let current = input;
  let unwrapped = current;
  chain.forEach((key) => {
    if (current && typeof current === 'object' && current[key]) {
      current = current[key];
      unwrapped = current;
    }
  });
  return unwrapped || input;
};

/**
 * Enhanced deep search for any value whose key matches candidates (case/underscore insensitive).
 * Searches ALL nested levels including arrays, with comprehensive key matching.
 * Returns the first non-empty value plus all matching keys encountered (for logging).
 */
const deepFindFirstWithKeys = (obj, keyCandidates, maxDepth = 20) => {
  const queue = [{ node: obj, depth: 0 }];
  const foundKeys = new Set();
  const allFoundKeys = [];
  let firstValue = null;
  const normalizedTargets = keyCandidates.map((k) => k.replace(/[^a-z0-9]/gi, '').toLowerCase());
  const visited = new WeakSet();

  while (queue.length) {
    const { node, depth } = queue.shift();
    if (!node || typeof node !== 'object' || depth > maxDepth) continue;
    if (visited.has(node)) continue; // Prevent circular references
    visited.add(node);

    if (Array.isArray(node)) {
      // Also search arrays
      node.forEach((item, idx) => {
        if (item && typeof item === 'object') {
          queue.push({ node: item, depth: depth + 1 });
        }
      });
      continue;
    }

    for (const [k, v] of Object.entries(node)) {
      const nk = k.replace(/[^a-z0-9]/gi, '').toLowerCase();
      if (normalizedTargets.includes(nk)) {
        foundKeys.add(k);
        allFoundKeys.push({ key: k, value: v });
        if (firstValue === null && hasValue(v)) {
          firstValue = v;
        }
      }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        queue.push({ node: v, depth: depth + 1 });
      } else if (Array.isArray(v)) {
        v.forEach((item, idx) => {
          if (item && typeof item === 'object') {
            queue.push({ node: item, depth: depth + 1 });
          }
        });
      }
    }
  }

  return { value: firstValue, foundKeys: Array.from(foundKeys), allFoundKeys };
};

/**
 * Extract server meta (IP, hosting, server software) using robust fallbacks and deep scanning.
 * Easily extendable by appending keys to the lists below.
 */
export const extractServerMeta = ({ site = {}, siteData = {}, securityInfo = {} } = {}) => {
  // Normalize payload roots and unwrap common containers
  const rootPayload = unwrapPayload(siteData?.data ?? siteData ?? {});
  const unwrappedSecurity = unwrapPayload(securityInfo ?? {});
  const meta = securityInfo?.meta || unwrappedSecurity?.meta || rootPayload?.meta || {};
  const overall = securityInfo?.overall || unwrappedSecurity?.overall || rootPayload?.overall || {};
  const env = rootPayload?.environment || {};
  const siteMeta = site?.metadata || {};

  const pickFirst = (getters) => {
    for (const getter of getters) {
      const value = getter();
      if (hasValue(value)) return value;
    }
    return null;
  };

  // Comprehensive key variants - matches backend extraction exactly
  const ipKeys = [
    'ip', 'ip_address', 'ipaddress', 'ipAddress', 'server_ip', 'serverIp', 'origin_ip', 'originIp',
    'resolvedip', 'resolvedIp', 'resolved_ip', 'address', 'remote_addr', 'remoteAddr', 'client_ip',
    'clientIp', 'hostip', 'hostIp', 'host_ip'
  ];
  const hostingKeys = [
    'hosting', 'hostingProvider', 'hosting_provider', 'host_provider', 'hostProvider', 'host_name',
    'hostName', 'provider', 'organization', 'org', 'host', 'hostname', 'hostName', 'isp',
    'hostingcompany', 'hosting_company', 'hostingCompany'
  ];
  const serverKeys = [
    'running_on', 'runningOn', 'server', 'server_type', 'serverType', 'serverSoftware', 'server_software',
    'web_server', 'webServer', 'webserver', 'software', 'platform', 'serverName', 'server_name',
    'server_info', 'serverInfo', 'powered_by', 'poweredBy', 'x-powered-by', 'technologies', 'technology'
  ];

  const searchSpace = { rootPayload, siteData, securityInfo, unwrappedSecurity, site, siteMeta, meta, overall, env };
  const ipDeep = deepFindFirstWithKeys(searchSpace, ipKeys);
  const hostingDeep = deepFindFirstWithKeys(searchSpace, hostingKeys);
  const serverDeep = deepFindFirstWithKeys(searchSpace, serverKeys);

  const ipAddress =
    pickFirst([
      () => rootPayload?.ip_address,
      () => rootPayload?.ipAddress,
      () => rootPayload?.ip,
      () => rootPayload?.server_ip,
      () => rootPayload?.serverIp,
      () => meta.ip,
      () => meta.ip_address,
      () => meta.origin_ip,
      () => meta.remote_addr,
      () => overall.ip,
      () => overall.ip_address,
      () => site?.ip,
      () => site?.server_ip,
      () => site?.serverIp,
      () => siteMeta?.ip,
      () => siteMeta?.ip_address,
      () => env?.ip,
      () => env?.ip_address,
      () => ipDeep.value,
    ]) || '';

  const hostingProvider =
    pickFirst([
      () => rootPayload?.hosting,
      () => rootPayload?.hostingProvider,
      () => rootPayload?.host_provider,
      () => rootPayload?.hosting_provider,
      () => rootPayload?.hostProvider,
      () => rootPayload?.host_name,
      () => rootPayload?.provider,
      () => env?.hosting,
      () => env?.host_name,
      () => env?.provider,
      () => meta.hosting,
      () => meta.host_provider,
      () => meta.host_name,
      () => meta.provider,
      () => overall.hosting,
      () => overall.host_provider,
      () => overall.host_name,
      () => overall.provider,
      () => site?.hosting,
      () => site?.host_provider,
      () => site?.hostProvider,
      () => site?.host_name,
      () => site?.provider,
      () => siteMeta?.hosting,
      () => siteMeta?.host_provider,
      () => siteMeta?.host_name,
      () => siteMeta?.provider,
      () => hostingDeep.value,
    ]) || '';

  const serverSoftware =
    pickFirst([
      () => rootPayload?.server,
      () => rootPayload?.web_server,
      () => rootPayload?.server_software,
      () => rootPayload?.serverSoftware,
      () => rootPayload?.webServer,
      () => rootPayload?.serverName,
      () => rootPayload?.runningOn,
      () => rootPayload?.running_on,
      () => rootPayload?.platform,
      () => rootPayload?.powered_by,
      () => env?.server,
      () => env?.runningOn,
      () => env?.running_on,
      () => env?.platform,
      () => env?.powered_by,
      () => meta.server,
      () => meta.runningOn,
      () => meta.running_on,
      () => meta.platform,
      () => meta.powered_by,
      () => overall.server,
      () => overall.runningOn,
      () => overall.running_on,
      () => overall.platform,
      () => overall.powered_by,
      () => meta.server_software,
      () => site?.server,
      () => site?.server_info,
      () => site?.serverInfo,
      () => site?.runningOn,
      () => site?.running_on,
      () => site?.platform,
      () => site?.powered_by,
      () => siteMeta?.server,
      () => siteMeta?.server_software,
      () => siteMeta?.runningOn,
      () => siteMeta?.running_on,
      () => siteMeta?.platform,
      () => siteMeta?.powered_by,
      () => serverDeep.value,
    ]) || '';

  console.log('[extractServerMeta] Deep extraction results', {
    siteId: site?._id,
    url: site?.url,
    ipAddress: ipAddress || 'NOT FOUND',
    hostingProvider: hostingProvider || 'NOT FOUND',
    serverSoftware: serverSoftware || 'NOT FOUND',
    searchSpaceKeys: {
      rootPayload: Object.keys(rootPayload || {}),
      siteData: Object.keys(siteData || {}),
      securityInfo: Object.keys(securityInfo || {}),
      meta: Object.keys(meta || {}),
      overall: Object.keys(overall || {}),
      env: Object.keys(env || {})
    },
    foundKeys: {
      ip: ipDeep.foundKeys,
      hosting: hostingDeep.foundKeys,
      server: serverDeep.foundKeys
    },
    allFoundKeys: {
      ip: ipDeep.allFoundKeys || [],
      hosting: hostingDeep.allFoundKeys || [],
      server: serverDeep.allFoundKeys || []
    }
  });

  return { ipAddress, hostingProvider, serverSoftware };
};

/**
 * Transforms monitoring data into Sucuri-style scan data format
 */
export const transformToSucuriFormat = (site, siteData = {}, securityInfo) => {
  // Always return data structure, even if minimal
  if (!siteData && !securityInfo) {
    // Return minimal structure based on site URL only
    const isHttps = site?.url?.startsWith('https://');
    const { ipAddress, hostingProvider, serverSoftware } = extractServerMeta({ site });
    return {
      summary: {
        site_outdated: false,
        not_blacklisted: true,
        blacklist_count: 9
      },
      ip_address: ipAddress || '',
      ip: ipAddress || '',
      hosting: hostingProvider || '',
      server: serverSoftware || '',
      runningOn: serverSoftware || '',
      cms: 'WordPress',
      cms_version: 'Unknown',
      powered_by: serverSoftware || '',
      security_checks: {
        malware_detected: false,
        spam_detected: false,
        defacement_detected: false,
        server_errors: false,
        site_outdated: false
      },
      blacklist_status: {
        google_safe_browsing: 'clean',
        mcafee: 'clean',
        sucuri_labs: 'clean',
        eset: 'clean',
        phishtank: 'clean',
        yandex: 'clean',
        opera: 'clean'
      },
      protection: {
        monitoring_detected: false,
        firewall_detected: false
      },
      outdated_software: [],
      security_headers: {
        x_frame_options: isHttps,
        strict_transport_security: isHttps,
        content_security_policy: false,
        server_banner_exposed: true,
        affected_pages: [site?.url || '']
      },
      hardening: {
        firewall_detected: false
      },
      risk_score: 0,
      risk_level: 'minimal'
    };
  }

  // Extract data from various possible formats
  const wpVersion = siteData?.wp_version || siteData?.wordpress_version || siteData?.wpVersion || siteData?.cms_version;
  const phpVersion = siteData?.php_version || siteData?.phpVersion;
  const plugins = siteData?.plugins || siteData?.plugin_list || [];
  const theme = siteData?.active_theme || siteData?.theme || siteData?.activeTheme;
  // CRITICAL: Use enhanced extraction to find values anywhere in the data structure
  // This ensures we catch values even if nested deep in siteData or securityInfo
  const { ipAddress, hostingProvider, serverSoftware } = extractServerMeta({ site, siteData, securityInfo });
  
  // Also check direct fields as fallback
  const normalizedMeta = {
    ip: ipAddress || siteData?.ip || siteData?.ip_address || '',
    hosting: hostingProvider || siteData?.hosting || siteData?.host_provider || '',
    runningOn: serverSoftware || siteData?.runningOn || siteData?.server || '',
    server: serverSoftware || siteData?.server || siteData?.runningOn || '',
    cms: siteData?.cms || (siteData?.wp_version ? 'WordPress' : ''),
    powered_by: siteData?.powered_by || siteData?.poweredBy || serverSoftware || ''
  };
  
  console.log('[transformToSucuriFormat] Meta extraction results', {
    siteId: site?._id,
    url: site?.url,
    extracted: {
      ipAddress: ipAddress || 'NOT FOUND',
      hostingProvider: hostingProvider || 'NOT FOUND',
      serverSoftware: serverSoftware || 'NOT FOUND'
    },
    normalized: {
      ip: normalizedMeta.ip || 'EMPTY',
      hosting: normalizedMeta.hosting || 'EMPTY',
      runningOn: normalizedMeta.runningOn || 'EMPTY'
    },
    siteDataKeys: Object.keys(siteData || {}),
    securityInfoKeys: Object.keys(securityInfo || {})
  });
  
  // Calculate comprehensive risk score using Sucuri-like methodology
  // Sucuri considers outdated software as medium risk by default
  const overall = securityInfo?.overall || {};
  
  // Check for outdated plugins - Sucuri's primary risk indicator
  // A plugin is outdated if it has an update available
  const outdatedPlugins = plugins.filter(p => {
    if (!p) return false;
    
    // Check various formats for update availability
    // Format 1: p.update is true or an object with new_version
    if (p.update === true) return true;
    if (p.update && typeof p.update === 'object') {
      if (p.update.new_version || p.update.newVersion) return true;
      if (p.update.id && p.update.slug) return true; // WordPress update object
    }
    
    // Format 2: Check if update field exists and is not false/null
    if (p.update !== false && p.update !== null && p.update !== undefined) {
      return true;
    }
    
    // Format 3: Check updatesList if available
    if (siteData?.updatesList && Array.isArray(siteData.updatesList)) {
      const pluginSlug = p.slug || p.plugin || '';
      if (pluginSlug && siteData.updatesList.some(u => (u.slug || u.plugin || '') === pluginSlug)) {
        return true;
      }
    }
    
    return false;
  });
  
  console.log('[transformToSucuriFormat] Outdated plugins detection', {
    siteId: site?._id,
    totalPlugins: plugins.length,
    outdatedCount: outdatedPlugins.length,
    outdatedPlugins: outdatedPlugins.map(p => ({ name: p.name || p.plugin, slug: p.slug, update: p.update }))
  });
  
  // Check SSL status
  const isHttps = site?.url?.startsWith('https://');
  
  // Preserve any security_checks coming from backend payload (needed for risk calculation)
  const rawSecurityChecks =
    securityInfo?.security_checks ||
    siteData?.security_checks ||
    siteData?.securityChecks ||
    {};
  
  // Extract malware flags/counts from backend (used in risk calculation and output)
  const backendMalwareFlag =
    rawSecurityChecks.malware_detected ??
    rawSecurityChecks.malware ??
    rawSecurityChecks.infected ??
    rawSecurityChecks.has_malware ??
    false;

  const backendMalwareCount =
    rawSecurityChecks.malware_count ??
    rawSecurityChecks.malwareCount ??
    rawSecurityChecks.threatCount ??
    0;
  
  // Start risk calculation - Conservative approach: only critical threats = High/Critical
  // Healthy sites should show Low (0-40) or Medium (41-60)
  // Only actual security threats show High (61-80) or Critical (81-100)
  let riskScore = 0;
  
  // Check for actual security threats first (malware, critical vulnerabilities)
  const hasMalware = backendMalwareFlag || backendMalwareCount > 0;
  const hasSpam = rawSecurityChecks.spam_detected;
  const hasDefacement = rawSecurityChecks.defacement_detected;
  const hasServerErrors = rawSecurityChecks.server_errors;
  
  // === CRITICAL SECURITY THREATS (High/Critical risk only) ===
  
  // 1. Malware detected - CRITICAL threat
  if (hasMalware) {
    riskScore = Math.max(riskScore, 85); // Critical risk
  }
  
  // 2. Defacement detected - CRITICAL threat
  if (hasDefacement) {
    riskScore = Math.max(riskScore, 90); // Critical risk
  }
  
  // 3. Critical vulnerabilities from security checks - HIGH risk
  // Only count if we have actual critical issues (not just warnings)
  const actualCriticalCount = Number(overall.criticalCount) || 0;
  if (actualCriticalCount > 0) {
    // Verify these are real critical security issues, not just outdated software warnings
    riskScore = Math.max(riskScore, 70 + Math.min(actualCriticalCount * 3, 20)); // High to Critical (70-90)
  }
  
  // 4. Spam detected - HIGH risk
  if (hasSpam) {
    riskScore = Math.max(riskScore, 75); // High risk
  }
  
  // 5. Server errors indicating compromise - HIGH risk
  if (hasServerErrors) {
    riskScore = Math.max(riskScore, 70); // High risk
  }
  
  // === MODERATE RISK FACTORS (Low to Medium risk) ===
  
  // 6. Outdated plugins - LOW to MEDIUM risk (not High)
  if (outdatedPlugins.length > 0 && riskScore < 60) {
    // Only assign Low to Medium risk for outdated plugins
    if (outdatedPlugins.length >= 10) {
      riskScore = Math.max(riskScore, 55); // Medium-high
    } else if (outdatedPlugins.length >= 5) {
      riskScore = Math.max(riskScore, 45); // Medium
    } else {
      riskScore = Math.max(riskScore, 35); // Low-Medium
    }
  }
  
  // 7. Outdated WordPress version - LOW to MEDIUM risk
  if (wpVersion && riskScore < 60) {
    const wpMajorVersion = parseInt(wpVersion.split('.')[0]);
    const wpMinorVersion = parseInt(wpVersion.split('.')[1]) || 0;
    
    if (wpMajorVersion < 5) {
      riskScore = Math.max(riskScore, 55); // Medium-high for very old WordPress
    } else if (wpMajorVersion < 6) {
      riskScore = Math.max(riskScore, 45); // Medium for old WordPress
    } else if (wpMajorVersion === 6 && wpMinorVersion < 9) {
      riskScore = Math.max(riskScore, 30); // Low-Medium for slightly outdated
    }
  }
  
  // 8. Outdated PHP version - LOW to MEDIUM risk
  if (phpVersion && riskScore < 60) {
    const phpVersionNum = parseFloat(phpVersion);
    if (phpVersionNum < 7.4) {
      riskScore = Math.max(riskScore, 50); // Medium for very old PHP
    } else if (phpVersionNum < 8.0) {
      riskScore = Math.max(riskScore, 40); // Low-Medium for PHP 7.x
    } else if (phpVersionNum < 8.2) {
      riskScore = Math.max(riskScore, 30); // Low for older PHP 8.x
    }
  }
  
  // 9. Missing HTTPS - MEDIUM risk (not High)
  if (!isHttps && riskScore < 60) {
    riskScore = Math.max(riskScore, 45); // Medium risk (not High)
  }
  
  // 10. Security headers missing - LOW risk
  const securityHeaders = securityInfo?.securityHeaders || {};
  let missingHeaders = 0;
  if (!securityHeaders.x_frame_options) missingHeaders++;
  if (!securityHeaders.strict_transport_security && isHttps) missingHeaders++;
  if (!securityHeaders.content_security_policy) missingHeaders++;
  
  if (missingHeaders >= 2 && riskScore < 50) {
    riskScore = Math.max(riskScore, 35); // Low-Medium
  } else if (missingHeaders === 1 && riskScore < 40) {
    riskScore = Math.max(riskScore, 25); // Low
  }
  
  // 11. Warning issues from security checks - LOW to MEDIUM risk
  if (overall.warningCount > 0 && riskScore < 50) {
    riskScore = Math.max(riskScore, Math.min(30 + (overall.warningCount * 2), 50)); // Low to Medium (30-50)
  }
  
  // 12. Site marked as outdated (but no critical issues) - LOW to MEDIUM risk
  const isOutdated = overall.status === 'warning' || overall.status === 'critical' || 
                    overall.criticalCount > 0 || overall.warningCount > 0 ||
                    outdatedPlugins.length > 0 ||
                    (wpVersion && parseInt(wpVersion.split('.')[0]) < 6) ||
                    (phpVersion && parseFloat(phpVersion) < 8.0);
  
  // Determine if site has outdated software
  const hasOutdatedSoftware = outdatedPlugins.length > 0 || 
                              (wpVersion && parseInt(wpVersion.split('.')[0]) < 6) ||
                              (phpVersion && parseFloat(phpVersion) < 8.0);
  
  // Only set to Low-Medium if no critical threats and site is outdated
  if (isOutdated && riskScore < 40 && !hasMalware && !hasDefacement && overall.criticalCount === 0) {
    riskScore = Math.max(riskScore, 35); // Low-Medium risk
  }
  
  // Default healthy sites to Low risk (10-30) if no issues found
  if (riskScore === 0 && !hasMalware && !hasDefacement && !hasSpam && overall.criticalCount === 0) {
    riskScore = 15; // Low risk for healthy sites
  }
  
  // Cap at 100
  riskScore = Math.min(100, Math.max(0, riskScore));
  
  const serverString = typeof serverSoftware === 'string' ? serverSoftware : serverSoftware?.name;
  
  console.log('[transformToSucuriFormat] Sucuri-style risk score calculation', {
    siteId: site?._id,
    finalRiskScore: riskScore,
    riskLevel: riskScore <= 20 ? 'minimal' : 
               riskScore <= 40 ? 'low' : 
               riskScore <= 60 ? 'medium' : 
               riskScore <= 80 ? 'high' : 'critical',
    factors: {
      outdatedPlugins: outdatedPlugins.length,
      wpVersion,
      phpVersion,
      isHttps,
      missingHeaders,
      criticalCount: overall.criticalCount,
      warningCount: overall.warningCount,
      isOutdated
    }
  });

  // Preserve any security_checks/outdated_software coming from backend payload
  // Note: rawSecurityChecks, backendMalwareFlag, and backendMalwareCount are already defined earlier

  const outgoing = {
    // Summary
    summary: {
      site_outdated: isOutdated,
      not_blacklisted: true, // Default, would come from actual blacklist scan
      blacklist_count: 9
    },
    
    // Website summary - CRITICAL: Use best available value from all sources
    // Priority: extracted > normalizedMeta > direct fields > empty
    ip_address: ipAddress || normalizedMeta.ip || siteData?.ip || siteData?.ip_address || '',
    ip: ipAddress || normalizedMeta.ip || siteData?.ip || siteData?.ip_address || '',
    hosting: hostingProvider || normalizedMeta.hosting || siteData?.hosting || siteData?.host_provider || '',
    server: serverString || normalizedMeta.server || siteData?.server || siteData?.runningOn || '',
    runningOn: serverString || normalizedMeta.runningOn || siteData?.runningOn || siteData?.server || '',
    cms: normalizedMeta.cms || siteData?.cms || (siteData?.wp_version ? 'WordPress' : '') || 'WordPress',
    cms_version: wpVersion || siteData?.cms_version || 'Unknown',
    powered_by: normalizedMeta.powered_by || siteData?.powered_by || siteData?.poweredBy || serverString || (typeof theme === 'string' ? theme : theme?.name) || '',
    poweredBy: normalizedMeta.powered_by || siteData?.powered_by || siteData?.poweredBy || serverString || (typeof theme === 'string' ? theme : theme?.name) || '',
    more_details: null,
    meta: {
      ipAddress: ipAddress || normalizedMeta.ip || siteData?.ip || siteData?.ip_address || '',
      hostingProvider: hostingProvider || normalizedMeta.hosting || siteData?.hosting || '',
      serverSoftware: serverString || normalizedMeta.runningOn || siteData?.runningOn || siteData?.server || '',
    },
    
    // Security checks
    // CRITICAL: never overwrite real malware/issue flags coming from backend
    security_checks: {
      // If backend provided malware flags/counts, preserve them; otherwise fall back to derived values
      malware_detected: Boolean(
        backendMalwareFlag ||
          backendMalwareCount > 0 ||
          rawSecurityChecks.status === 'infected' ||
          rawSecurityChecks.status === 'malware'
      ),
      malware_count:
        Number(backendMalwareCount) ||
        (backendMalwareFlag ? 1 : 0),
      spam_detected:
        rawSecurityChecks.spam_detected ??
        rawSecurityChecks.spam ??
        false,
      defacement_detected:
        rawSecurityChecks.defacement_detected ??
        rawSecurityChecks.defaced ??
        false,
      server_errors:
        rawSecurityChecks.server_errors ??
        rawSecurityChecks.serverError ??
        false,
      site_outdated:
        rawSecurityChecks.site_outdated ??
        isOutdated
    },
    
    // Blacklist status (would come from actual blacklist scan)
    blacklist_status: {
      google_safe_browsing: 'clean',
      mcafee: 'clean',
      sucuri_labs: 'clean',
      eset: 'clean',
      phishtank: 'clean',
      yandex: 'clean',
      opera: 'clean'
    },
    
    // Protection status
    protection: {
      monitoring_detected: false, // Would come from actual scan
      firewall_detected: false
    },
    
    // Outdated software
    // Merge any backend-provided list with our derived outdated_software list
    outdated_software: hasOutdatedSoftware ? [
      ...(Array.isArray(siteData?.outdated_software) ? siteData.outdated_software : []),
      ...(outdatedPlugins.length > 0 ? outdatedPlugins.slice(0, 3).map(p => ({
        name: p.name || p.plugin || 'Unknown Plugin',
        version: p.version,
        vulnerability_link: null
      })) : []),
      ...(serverString && serverString.includes('nginx') && !serverString.includes('1.17.3') ? [{
        name: 'Nginx',
        version: serverString.match(/\d+\.\d+\.\d+/)?.[0] || 'Unknown',
        vulnerability_link: '#'
      }] : [])
    ] : (Array.isArray(siteData?.outdated_software) ? siteData.outdated_software : []),
    
    // Security headers (from actual header scan - use data from backend if available)
    security_headers: {
      x_frame_options: siteData?.security_headers?.x_frame_options !== undefined ? siteData.security_headers.x_frame_options : false,
      x_content_type_options: siteData?.security_headers?.x_content_type_options !== undefined ? siteData.security_headers.x_content_type_options : false,
      strict_transport_security: siteData?.security_headers?.strict_transport_security !== undefined ? siteData.security_headers.strict_transport_security : false,
      content_security_policy: siteData?.security_headers?.content_security_policy !== undefined ? siteData.security_headers.content_security_policy : false,
      server_banner_exposed: siteData?.security_headers?.server_banner_exposed !== undefined ? siteData.security_headers.server_banner_exposed : true,
      affected_pages: siteData?.security_headers?.affected_pages || [site?.url || '']
    },
    
    // Hardening
    hardening: {
      firewall_detected: false
    },
    
    // Risk score - Sucuri style calculation
    risk_score: riskScore,
    risk_level: riskScore <= 20 ? 'minimal' : 
                riskScore <= 40 ? 'low' : 
                riskScore <= 60 ? 'medium' : 
                riskScore <= 80 ? 'high' : 'critical',
    // Store detailed risk factors for debugging
    _riskFactors: {
      outdatedPlugins: outdatedPlugins.length,
      wpVersion,
      phpVersion,
      isHttps,
      missingHeaders,
      criticalCount: overall.criticalCount,
      warningCount: overall.warningCount
    }
  };

  console.log('[transformToSucuriFormat] Final normalized output', {
    siteId: site?._id,
    url: site?.url,
    final: {
      ip: outgoing.ip || outgoing.ip_address || 'EMPTY',
      ip_address: outgoing.ip_address || outgoing.ip || 'EMPTY',
      hosting: outgoing.hosting || 'EMPTY',
      runningOn: outgoing.runningOn || outgoing.server || 'EMPTY',
      server: outgoing.server || outgoing.runningOn || 'EMPTY',
      cms: outgoing.cms || 'EMPTY',
      powered_by: outgoing.powered_by || outgoing.poweredBy || 'EMPTY'
    },
    extracted: {
      ipAddress: ipAddress || 'NOT FOUND',
      hostingProvider: hostingProvider || 'NOT FOUND',
      serverSoftware: (typeof serverSoftware === 'string' ? serverSoftware : serverSoftware?.name) || 'NOT FOUND'
    },
    normalizedMeta: {
      ip: normalizedMeta.ip || 'EMPTY',
      hosting: normalizedMeta.hosting || 'EMPTY',
      runningOn: normalizedMeta.runningOn || 'EMPTY'
    },
    outgoingMeta: outgoing.meta,
  });

  return outgoing;
};


